import { Textarea } from '@/components/common/Textarea'
import { RadioGroup } from '@/components/common/RadioGroup'
import { DentalChart } from '@/components/common/DentalChart'
import { GradedToothSelector } from '@/components/common/GradedToothSelector'
import { StepShell, StepGrid } from '../StepShell'
import {
  ORAL_HYGIENE_OPTIONS,
  GINGIVA_OPTIONS,
  RECESSION_OPTIONS,
  CALCULUS_STAIN_OPTIONS,
} from '@/lib/constants'

const DEFAULT = {
  oral_hygiene_status: null,
  gingiva: null,
  recession: null,
  calculus_stains: null,
  caries: { present: false },
  tooth_mobility: { present: false },
  missing_teeth: { adult_teeth: [], child_teeth: [] },
  soft_tissue_findings: '',
  occlusion: '',
}

export function IntraOralStep({ value, onChange }) {
  const v = { ...DEFAULT, ...(value || {}) }
  const set = (patch) => onChange?.({ ...v, ...patch })

  return (
    <StepShell
      eyebrow="Section 8 — Phase 3"
      title="Intra oral examination"
      description="Findings inside the mouth — graded selections and the affected teeth."
    >
      <StepGrid>
        <RadioGroup
          label="Oral hygiene status"
          options={ORAL_HYGIENE_OPTIONS}
          value={v.oral_hygiene_status}
          onChange={(val) => set({ oral_hygiene_status: val })}
          columns={3}
          allowClear
        />
        <RadioGroup
          label="Gingiva"
          options={GINGIVA_OPTIONS}
          value={v.gingiva}
          onChange={(val) => set({ gingiva: val })}
          columns={2}
          allowClear
        />
        <RadioGroup
          label="Recession"
          options={RECESSION_OPTIONS}
          value={v.recession}
          onChange={(val) => set({ recession: val })}
          columns={3}
          allowClear
        />
        <RadioGroup
          label="Calculus / stains"
          options={CALCULUS_STAIN_OPTIONS}
          value={v.calculus_stains}
          onChange={(val) => set({ calculus_stains: val })}
          columns={3}
          allowClear
        />
      </StepGrid>

      <div className="rounded-2xl bg-cream/60 p-4 ring-1 ring-ink-100 sm:p-5">
        <GradedToothSelector
          label="Dental caries"
          hint="Mark whether caries is present, pick the grade tab, then tap each affected tooth."
          value={v.caries}
          onChange={(val) => set({ caries: val })}
          grades={['grade_1', 'grade_2', 'grade_3', 'grade_4', 'grade_5', 'grade_6']}
        />
      </div>

      <div className="rounded-2xl bg-cream/60 p-4 ring-1 ring-ink-100 sm:p-5">
        <GradedToothSelector
          label="Tooth mobility"
          hint="Pick a grade tab, then tap the mobile teeth."
          value={v.tooth_mobility}
          onChange={(val) => set({ tooth_mobility: val })}
          grades={['grade_1', 'grade_2', 'grade_3']}
        />
      </div>

      <div className="rounded-2xl bg-cream/60 p-4 ring-1 ring-ink-100 sm:p-5">
        <p className="mb-3 text-sm font-semibold text-ink-800">Missing teeth</p>
        <DentalChart
          value={v.missing_teeth}
          onChange={(val) => set({ missing_teeth: val })}
        />
      </div>

      <StepGrid>
        <Textarea
          label="Soft tissue findings"
          rows={2}
          value={v.soft_tissue_findings}
          onChange={(e) => set({ soft_tissue_findings: e.target.value })}
          placeholder="Ulcer / lesion / patch — site, size"
        />
        <Textarea
          label="Occlusion"
          rows={2}
          value={v.occlusion}
          onChange={(e) => set({ occlusion: e.target.value })}
          placeholder="Class I / II / III"
        />
      </StepGrid>
    </StepShell>
  )
}
